import React, { useState } from 'react';
import { ShieldIcon, ChevronDownIcon, ChevronUpIcon, CheckCircle2, FileText, ClipboardList, Mail, Scale, Phone, AlertTriangleIcon, ShieldAlertIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface BonusDetail {
  title: string;
  subtitle?: string;
  value: string;
  description: string[];
  listTitle?: string;
  items: {
    icon?: string;
    text: string;
  }[];
  whoFor?: string;
  whatReplaces?: string;
  testimonial?: string;
  disclaimer?: string;
}
const bonusDetails: Record<string, BonusDetail> = {
  bonus1: {
    title: 'The Letter of Intent Template',
    subtitle: 'Reverse Mortgage Extension',
    value: '$9.99',
    description: ['If the house you inherited has a reverse mortgage, you have a 30-day window from the date of the Due and Payable notice to respond — or the bank moves forward with foreclosure proceedings.', "Most heirs don't find out until week three.", "This fill-in template — two pages, sent by certified mail — legally buys you up to 6 months to decide what to do with the property. Sell, refinance, or keep it. On your timeline, not the bank's."],
    listTitle: "What's included in the template:",
    items: [{
      icon: 'filetext',
      text: 'Complete Letter of Intent — ready to fill in and send'
    }, {
      icon: 'clipboard',
      text: 'Pre-send checklist — everything you need to gather first'
    }, {
      icon: 'mail',
      text: 'Certified mail sending instructions — step by step'
    }, {
      icon: 'scale',
      text: 'Key terms glossary — so you understand every word'
    }, {
      icon: 'phone',
      text: 'Free HUD resources — including the free counseling hotline'
    }],
    disclaimer: 'For educational purposes only. We recommend consulting with a licensed attorney before sending any legal correspondence.'
  },
  bonus2: {
    title: 'The Bank Call Script Pack',
    subtitle: '7 Scripts To Make Them Take You Seriously',
    value: '$9.99',
    description: ['The first phone call to the bank is where most heirs freeze.', "You don't know who to ask for. You don't know what to say. And the second you sound unsure — they put you on hold, transfer you three times, and you hang up with nothing.", 'This Script Pack ends that.', 'Inside, you get 7 word-for-word scripts that tell you exactly what to say — and exactly how to close each call with a case number, a document checklist, and a confirmed next step in writing.'],
    listTitle: "Here's what's inside:",
    items: [{
      text: 'Script 1: Open the case and get routed to the right department instantly'
    }, {
      text: 'Script 2: Get the exact document checklist (so you send it complete the first time)'
    }, {
      text: 'Script 3: Force everything into writing (no more "they told me")'
    }, {
      text: 'Script 4: Escalate to a supervisor without an argument'
    }, {
      text: 'Script 5: Confirm document receipt so nothing "gets lost"'
    }, {
      text: 'Script 6: Handle the vacancy/occupancy question safely'
    }, {
      text: 'Script 7: Use deadline language to trigger urgent action'
    }, {
      text: 'BONUS: A copy/paste Call Log template to document every call'
    }, {
      text: 'BONUS: A 1-page pre-call printable checklist'
    }],
    whoFor: "Anyone who has avoided calling the bank because they don't know what to say.",
    whatReplaces: '3 hours of back-and-forth calls, wrong departments, and vague answers.',
    testimonial: "\"The scripts gave me the exact words. I went from 'we can\'t speak to you' to 'let me open a case' in one call.\" — Verified reader"
  },
  bonus3: {
    title: 'The 72-Hour House Lockdown Checklist',
    subtitle: 'Secure the Property Without Losing Money',
    value: '$9.99',
    description: ['The moment you have access to that house — the clock starts.', 'Unsecured homes get broken into. Small leaks become $8,000 water damage claims. Relatives "stop by" and things disappear. Mail piles up and deadlines get missed.', 'The 72-Hour Lockdown Checklist gives you a step-by-step action plan for the first 3 days — so none of that happens to you.'],
    listTitle: "Here's what's inside:",
    items: [{
      text: 'The exact "photos first" protocol (before you touch anything)'
    }, {
      text: 'Hour-by-hour action plan: 0–2 hrs / 2–6 hrs / 6–12 hrs / 12–24 hrs / 24–48 hrs / 48–72 hrs'
    }, {
      text: 'What to bring on your first visit (10-item pack list)'
    }, {
      text: 'How to find and collect the documents that control the timeline'
    }, {
      text: 'The 3 utility rules: what to keep on, what to turn off, and what to never touch blindly'
    }, {
      text: 'The "minimum inventory" method to prevent family conflict over belongings'
    }, {
      text: 'A 1-page printable checklist to bring to the house'
    }],
    whoFor: "Anyone who hasn't visited the house yet — or anyone who went in without a plan and needs to go back with one.",
    whatReplaces: 'Expensive mistakes, family arguments, and missed deadlines that happen in the first 72 hours.',
    testimonial: '"I didn\'t know I needed this until I was standing in the driveway with no idea where to start. This was the plan I didn\'t have." — Verified reader'
  },
  bonus4: {
    title: 'The Sell vs. Rent vs. Hold Decision Calculator',
    subtitle: 'Decide in 20 Minutes (Without Family Fights)',
    value: '$9.99',
    description: ['Every inherited house eventually comes down to one question:', 'What do we do with it?', 'And that question — without numbers — turns into arguments. Everyone has an opinion. Nobody has a plan.', 'This calculator changes that in 20 minutes.', "You fill in 6 basic numbers. It shows you the realistic monthly bleed, the estimated net from selling, the cash flow from renting, and the true cost of waiting. Then it generates a 1-page decision summary you can share with the family — so you're arguing from math, not emotion."],
    listTitle: "Here's what's inside:",
    items: [{
      text: 'Step-by-step worksheet (beginner-friendly, no formulas to figure out)'
    }, {
      text: 'The "Monthly Bleed" formula: what the house costs you every month you wait'
    }, {
      text: 'SELL scenario: estimated net after costs, debts, repairs, and holding time'
    }, {
      text: 'RENT scenario: realistic cash flow + break-even timeline'
    }, {
      text: 'HOLD scenario: total cost of waiting 3 / 6 / 12 months'
    }, {
      text: '3 pre-filled worked examples (sell wins / rent works / hold temporarily)'
    }, {
      text: 'A family-ready 1-page Decision Summary to present with confidence'
    }, {
      text: 'The "Family Fight Prevention" script to de-escalate disagreements with data'
    }],
    whoFor: 'Any heir or executor who\'s stuck in "we can\'t agree" mode.',
    whatReplaces: 'A $500 consultation with a real estate professional — for $0.',
    testimonial: '"We had been going back and forth for 3 months. I filled this out in 20 minutes. We made the decision that day." — Verified reader'
  },
  bump1: {
    title: "The Executor's Shoebox Sort",
    subtitle: 'Organize 20+ Years in 1 Afternoon',
    value: '$9.87',
    description: ['Most heirs waste 40+ hours digging through "junk" mail and old desks, only to miss the ONE document the bank actually needs.', 'This tactical guide shows you how to organize 20+ years of a parent\'s records in a single afternoon. You\'ll get the "5-Bin System" used by professional estate managers to find deeds, hidden insurance policies, and account passwords without the mental breakdown.'],
    items: [],
    whoFor: 'Any heir facing boxes, drawers, or filing cabinets full of a lifetime of paperwork.',
    whatReplaces: '40+ hours of confused sorting and the risk of missing the one document the bank needs.'
  },
  bump2: {
    title: 'The Family Meeting Survival Script',
    subtitle: 'Stop Arguments Before They Start',
    value: '$9.87',
    description: ['Nothing destroys a family faster than an inherited house. One sibling wants to keep it, one wants to sell it, and one is already trying to move in.', 'You\'ll get the exact, word-for-word scripts to lead your first family meeting with authority. Learn how to present the "Holding Costs" so everyone sees the math, and use our "Conflict De-escalators" to shut down old grudges before they turn into a lawsuit.'],
    items: [],
    whoFor: 'Any heir who needs to lead a family conversation about the house without it turning into a fight.',
    whatReplaces: 'Years of family tension and potentially thousands in legal fees from unresolved disagreements.'
  }
};
function getIconForItem(iconType?: string) {
  const cls = 'w-5 h-5 text-gold shrink-0 mt-0.5';
  switch (iconType) {
    case 'filetext':
      return <FileText className={cls} data-id="element-272" />;
    case 'clipboard':
      return <ClipboardList className={cls} data-id="element-273" />;
    case 'mail':
      return <Mail className={cls} data-id="element-274" />;
    case 'scale':
      return <Scale className={cls} data-id="element-275" />;
    case 'phone':
      return <Phone className={cls} data-id="element-276" />;
    default:
      return <CheckCircle2 className={cls} data-id="element-277" />;
  }
}
function BonusExpandable({
  detail
}: {
  detail: BonusDetail;
}) {
  return <div className="px-6 pb-6 pt-2 bg-warm-white/50" data-id="element-278">
      <div className="max-w-3xl space-y-4" data-id="element-279">
        {detail.subtitle && <p className="text-navy-light font-heading italic text-lg" data-id="element-280">
            {detail.subtitle}
          </p>}
        {detail.description.map((p, i) => <p key={i} className="text-text-dark font-body leading-relaxed" data-id="element-281">
            {p}
          </p>)}
        {detail.listTitle && <h4 className="text-lg font-bold text-navy mt-4" data-id="element-282">
            {detail.listTitle}
          </h4>}
        {detail.items.length > 0 && <ul className="space-y-2" data-id="element-283">
            {detail.items.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-284">
                {getIconForItem(item.icon)}
                <span className="text-text-dark" data-id="element-285">{item.text}</span>
              </li>)}
          </ul>}
        {(detail.whoFor || detail.whatReplaces) && <div className="bg-white p-4 rounded-lg border border-slate-light/20 space-y-2 mt-4" data-id="element-286">
            {detail.whoFor && <p className="text-text-dark text-sm" data-id="element-287">
                <strong className="text-navy" data-id="element-288">Who this is for:</strong>{' '}
                {detail.whoFor}
              </p>}
            {detail.whatReplaces && <p className="text-text-dark text-sm" data-id="element-289">
                <strong className="text-navy" data-id="element-290">What it replaces:</strong>{' '}
                {detail.whatReplaces}
              </p>}
          </div>}
        {detail.testimonial && <blockquote className="border-l-4 border-gold pl-4 italic text-slate text-sm mt-4" data-id="element-291">
            {detail.testimonial}
          </blockquote>}
        {detail.disclaimer && <p className="text-xs text-text-muted italic mt-4" data-id="element-292">
            {detail.disclaimer}
          </p>}
      </div>
    </div>;
}
interface GuaranteeSectionProps {
  bump1Selected: boolean;
  setBump1Selected: (val: boolean) => void;
  bump2Selected: boolean;
  setBump2Selected: (val: boolean) => void;
  displayTotal: string;
}
export function GuaranteeSection({
  bump1Selected,
  setBump1Selected,
  bump2Selected,
  setBump2Selected,
  displayTotal
}: GuaranteeSectionProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => {
    setExpanded(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const rows = [{
    key: 'kit',
    label: 'The Inherited House Survival Kit (35-page PDF)',
    value: '$27',
    expandable: false
  }, {
    key: 'bonus1',
    label: 'BONUS #1: The Letter of Intent',
    value: '$9.99',
    expandable: true
  }, {
    key: 'bonus2',
    label: 'BONUS #2: The Bank Call Script Pack',
    value: '$9.99',
    expandable: true
  }, {
    key: 'bonus3',
    label: 'BONUS #3: The 72-Hour House Lockdown Checklist',
    value: '$9.99',
    expandable: true
  }, {
    key: 'bonus4',
    label: 'BONUS #4: The Sell vs. Rent vs. Hold Calculator',
    value: '$9.99',
    expandable: true
  }];
  return <section className="bg-warm-white-dark py-12 md:py-20 px-6" data-id="element-293">
      <div className="max-w-4xl mx-auto" data-id="element-294">
        <h2 className="text-3xl md:text-4xl font-bold text-navy text-center mb-12" data-id="element-295">
          TOTAL VALUE STACK
        </h2>

        {/* Value Stack */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-light/30 overflow-hidden mb-12" data-id="element-296">
          {/* Header */}
          <div className="bg-navy text-white flex" data-id="element-297">
            <div className="py-4 px-6 font-heading text-xl font-semibold flex-1" data-id="element-298">
              What You Get
            </div>
            <div className="py-4 px-6 font-heading text-xl font-semibold text-right w-28" data-id="element-299">
              Value
            </div>
          </div>

          {/* Bonus Rows */}
          <div className="font-body text-text-dark divide-y divide-slate-light/20" data-id="element-300">
            {rows.map(row => <div key={row.key} data-id="element-301">
                <div className="flex items-center" data-id="element-302">
                  <div className="py-4 px-6 text-lg flex-1" data-id="element-303">{row.label}</div>
                  <div className="py-4 px-6 text-right font-medium w-28" data-id="element-304">
                    {row.expandable ? <div className="flex flex-col items-end" data-id="element-305">
                        <span className="line-through text-slate text-lg" data-id="element-306">
                          {row.value}
                        </span>
                        <span className="text-green-600 text-xs font-bold" data-id="element-307">
                          Yours Free Today!
                        </span>
                      </div> : <span className="text-lg" data-id="element-308">{row.value}</span>}
                  </div>
                </div>
                {row.expandable && <>
                    <div className="px-6 pb-3" data-id="element-309">
                      <button onClick={() => toggle(row.key)} className="text-accent hover:text-accent-hover font-medium text-sm flex items-center gap-1 transition-colors cursor-pointer" data-id="element-310">
                        {expanded[row.key] ? 'Show less' : 'Read more'}
                        {expanded[row.key] ? <ChevronUpIcon className="w-4 h-4" data-id="element-311" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-312" />}
                      </button>
                    </div>
                    <AnimatePresence data-id="element-313">
                      {expanded[row.key] && bonusDetails[row.key] && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} className="overflow-hidden" data-id="element-314">
                          <BonusExpandable detail={bonusDetails[row.key]} data-id="element-315" />
                        </motion.div>}
                    </AnimatePresence>
                  </>}
              </div>)}

            {/* Order Bump #1 */}
            <motion.div animate={{
            boxShadow: bump1Selected ? '0 0 0 2px #b8860b' : ['0 0 0 0px rgba(184,134,11,0)', '0 0 16px 3px rgba(184,134,11,0.3)', '0 0 0 0px rgba(184,134,11,0)']
          }} transition={bump1Selected ? {
            duration: 0.2
          } : {
            duration: 2.5,
            repeat: Infinity,
            repeatDelay: 1
          }} className={`mx-3 my-3 rounded-lg overflow-hidden cursor-pointer ${bump1Selected ? 'border-2 border-gold bg-amber-50/50' : 'border border-amber-300/40 bg-amber-50/30'}`} onClick={() => setBump1Selected(!bump1Selected)} data-id="element-316">
              <div className="bg-amber-600 text-white py-2 px-4 flex items-center gap-2 justify-center" data-id="element-317">
                <AlertTriangleIcon className="w-4 h-4" data-id="element-318" />
                <span className="font-bold text-sm uppercase tracking-wide" data-id="element-319">
                  Add to order: The Executor's Shoebox Sort — $9.87
                </span>
              </div>
              <div className="p-4 flex items-start gap-4" data-id="element-320">
                <img src="https://cdn.magicpatterns.com/uploads/han6YxcQyCwtddeWkfjd84/Oder_Bump_1_The_Shoebox.png" alt="The Executor's Shoebox Sort" className="w-20 md:w-24 rounded-lg object-cover shrink-0" data-id="element-321" />
                <div className="flex-1" data-id="element-322">
                  <label className="flex items-center gap-3 cursor-pointer mb-2" data-id="element-323">
                    <input type="checkbox" checked={bump1Selected} onChange={() => setBump1Selected(!bump1Selected)} className="w-5 h-5 accent-gold rounded cursor-pointer shrink-0" data-id="element-324" />
                    <span className="text-navy font-bold text-sm md:text-base" data-id="element-325">
                      Yes! Add this to my order for $9.87
                    </span>
                  </label>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-326">
                    Organize 20+ years of records in a single afternoon with the
                    "5-Bin System."
                  </p>
                  <button onClick={e => {
                  e.stopPropagation();
                  toggle('bump1');
                }} className="text-accent hover:text-accent-hover font-medium text-xs flex items-center gap-1 mt-2 transition-colors cursor-pointer" data-id="element-327">
                    {expanded.bump1 ? 'Show less' : 'Read more'}
                    {expanded.bump1 ? <ChevronUpIcon className="w-3 h-3" data-id="element-328" /> : <ChevronDownIcon className="w-3 h-3" data-id="element-329" />}
                  </button>
                </div>
              </div>
              <AnimatePresence data-id="element-330">
                {expanded.bump1 && bonusDetails.bump1 && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-331">
                    <BonusExpandable detail={bonusDetails.bump1} data-id="element-332" />
                  </motion.div>}
              </AnimatePresence>
            </motion.div>

            {/* Order Bump #2 */}
            <motion.div animate={{
            boxShadow: bump2Selected ? '0 0 0 2px #b8860b' : ['0 0 0 0px rgba(184,134,11,0)', '0 0 16px 3px rgba(184,134,11,0.3)', '0 0 0 0px rgba(184,134,11,0)']
          }} transition={bump2Selected ? {
            duration: 0.2
          } : {
            duration: 2.5,
            repeat: Infinity,
            repeatDelay: 1.5
          }} className={`mx-3 my-3 rounded-lg overflow-hidden cursor-pointer ${bump2Selected ? 'border-2 border-gold bg-amber-50/50' : 'border border-amber-300/40 bg-amber-50/30'}`} onClick={() => setBump2Selected(!bump2Selected)} data-id="element-333">
              <div className="bg-amber-600 text-white py-2 px-4 flex items-center gap-2 justify-center" data-id="element-334">
                <ShieldAlertIcon className="w-4 h-4" data-id="element-335" />
                <span className="font-bold text-sm uppercase tracking-wide" data-id="element-336">
                  Add to order: The Family Meeting Survival Script — $9.87
                </span>
              </div>
              <div className="p-4 flex items-start gap-4" data-id="element-337">
                <img src="https://cdn.magicpatterns.com/uploads/ajZvVkoroUwGDdDMp6Kjgh/Order_Bump_2_The_Family_Meeting.png" alt="The Family Meeting Survival Script" className="w-20 md:w-24 rounded-lg object-cover shrink-0" data-id="element-338" />
                <div className="flex-1" data-id="element-339">
                  <label className="flex items-center gap-3 cursor-pointer mb-2" data-id="element-340">
                    <input type="checkbox" checked={bump2Selected} onChange={() => setBump2Selected(!bump2Selected)} className="w-5 h-5 accent-gold rounded cursor-pointer shrink-0" data-id="element-341" />
                    <span className="text-navy font-bold text-sm md:text-base" data-id="element-342">
                      Yes! Add this to my order for $9.87
                    </span>
                  </label>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-343">
                    Word-for-word scripts to lead your first family meeting with
                    authority.
                  </p>
                  <button onClick={e => {
                  e.stopPropagation();
                  toggle('bump2');
                }} className="text-accent hover:text-accent-hover font-medium text-xs flex items-center gap-1 mt-2 transition-colors cursor-pointer" data-id="element-344">
                    {expanded.bump2 ? 'Show less' : 'Read more'}
                    {expanded.bump2 ? <ChevronUpIcon className="w-3 h-3" data-id="element-345" /> : <ChevronDownIcon className="w-3 h-3" data-id="element-346" />}
                  </button>
                </div>
              </div>
              <AnimatePresence data-id="element-347">
                {expanded.bump2 && bonusDetails.bump2 && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-348">
                    <BonusExpandable detail={bonusDetails.bump2} data-id="element-349" />
                  </motion.div>}
              </AnimatePresence>
            </motion.div>

            {/* Total Value */}
            <div className="flex bg-warm-white font-semibold" data-id="element-350">
              <div className="py-4 px-6 text-right text-lg flex-1" data-id="element-351">
                TOTAL VALUE
              </div>
              <div className="py-4 px-6 text-right line-through text-slate text-lg w-36" data-id="element-352">
                $66.96
              </div>
            </div>

            {/* Dynamic Price */}
            <div className="flex bg-navy-light text-white font-bold" data-id="element-353">
              <div className="py-6 px-6 text-right text-xl flex-1" data-id="element-354">
                Your Price Today
              </div>
              <motion.div key={displayTotal} initial={{
              scale: 1.3,
              color: '#fbbf24'
            }} animate={{
              scale: 1,
              color: '#b8860b'
            }} transition={{
              duration: 0.4
            }} className="py-6 px-6 text-right text-3xl w-36" data-id="element-355">
                {displayTotal}
              </motion.div>
            </div>
          </div>
        </div>

        {/* Guarantee Block */}
        <div className="bg-white rounded-xl p-8 md:p-12 shadow-sm border border-slate-light/30 text-center" data-id="element-356">
          <div className="flex justify-center mb-6" data-id="element-357">
            <ShieldIcon className="w-16 h-16 text-navy" data-id="element-358" />
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-navy mb-6" data-id="element-359">
            30-Day "No Confusion" Guarantee
          </h3>
          <p className="text-lg md:text-xl text-text-dark font-body leading-relaxed max-w-3xl mx-auto" data-id="element-360">
            Read the entire kit. Follow the steps. If you don't feel
            significantly more confident and clear about what to do with your
            inherited property, email us within 30 days and we'll refund every
            penny. No questions. No forms. No waiting.
          </p>
        </div>
      </div>
    </section>;
}